import { SortDirection } from 'components/Table/types';
import { IVariables } from 'store/Model/reducers/model';
import { sortTableRows } from 'utils/sortTableRows';

// Only variables with this prefix are shown as specs - the rest are internals
// the user has no business seeing.
export const SPEC_VARIABLE_PREFIX = 'V_';

export interface IFormattedVariable {
  name: string;
  value: string;
}

// V_Fabric_width -> Fabric width
const formatVariableName = (variableKey: string): string =>
  variableKey.slice(SPEC_VARIABLE_PREFIX.length).replace(/_/g, ' ');

// The table renders cell values as they are, so anything the model computed
// that is not a primitive has to be flattened first.
const formatVariableValue = (value: unknown): string => {
  if (value === null || value === undefined) {
    return '';
  }
  if (typeof value === 'object') {
    return JSON.stringify(value);
  }
  return String(value);
};

// Kept off the page whatever their value: MPR variables are internal
// bookkeeping, and discounts are not the client's business here.
//
// MPR is matched per underscore separated part of the name rather than as a
// plain substring, so a variable that merely contains the letters
// (V_Compressor_x) is not swept up with it. "discount" is distinctive enough
// to match anywhere in the name.
const HIDDEN_VARIABLE_NAME_PART = 'mpr';
const HIDDEN_VARIABLE_NAME_SUBSTRING = 'discount';

const isHiddenVariable = (variableKey: string): boolean => {
  const lowerCaseKey = variableKey.toLowerCase();
  return (
    lowerCaseKey.split('_').includes(HIDDEN_VARIABLE_NAME_PART) ||
    lowerCaseKey.includes(HIDDEN_VARIABLE_NAME_SUBSTRING)
  );
};

// A variable the model left empty says nothing to the user, so it is left out
// rather than listed as a name with a blank next to it. 0 and false are
// computed values and stay.
const isSpecVariable = (variables: IVariables, variableKey: string): boolean =>
  variableKey.startsWith(SPEC_VARIABLE_PREFIX) &&
  !isHiddenVariable(variableKey) &&
  formatVariableValue(variables[variableKey]?.value).trim() !== '';

export const hasAnySpecVariable = (variables: IVariables | null): boolean =>
  !!variables &&
  Object.keys(variables).some((variableKey: string) =>
    isSpecVariable(variables, variableKey)
  );

export const formatSpecVariables = (
  variables: IVariables | null
): IFormattedVariable[] => {
  if (!variables) {
    return [];
  }

  return Object.keys(variables)
    .filter((variableKey: string) => isSpecVariable(variables, variableKey))
    .map((variableKey: string) => ({
      name: formatVariableName(variableKey),
      value: formatVariableValue(variables[variableKey]?.value),
    }));
};

// sortBy holds whatever sortableFieldsMapper derived from the column header,
// which for Name and Value is the row key itself.
const isSortableField = (
  sortBy: string
): sortBy is keyof IFormattedVariable => {
  return sortBy === 'name' || sortBy === 'value';
};

// The backend returns variables unsorted and the page holds all of them at
// once, so the sorting the table header asks for happens here.
export const sortSpecVariables = (
  formattedVariables: IFormattedVariable[],
  sortBy: string,
  sortDirection: SortDirection | undefined
): IFormattedVariable[] =>
  sortTableRows(
    formattedVariables,
    isSortableField(sortBy) ? sortBy : undefined,
    sortDirection
  );
